import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Button, Icon, Input } from '@dataroom/shared';
import { useRenameRoom } from '@/api/hooks';
import { apiMessage } from '@/lib/errors';

/** The API's limit (PATCH /rooms/:roomId/name): 1–120 characters after trimming. */
const MAX_ROOM_NAME_LENGTH = 120;

/** Why `draft` cannot be saved, in plain words, or null. The API checks again. */
function roomNameProblem(draft: string): string | null {
  const name = draft.trim();
  if (name.length === 0) return 'Enter a name for the room.';
  if (name.length > MAX_ROOM_NAME_LENGTH) {
    return `Keep the room name to ${MAX_ROOM_NAME_LENGTH} characters or fewer.`;
  }
  return null;
}

const HEADING_CLASS =
  'break-words font-serif text-2xl font-semibold leading-8 tracking-tight text-balance md:text-[28px] md:leading-9';

/**
 * The room header's name. For the room's own admin (`canRename`) a pencil
 * "Rename" control sits beside it and swaps the heading for an inline field:
 * Enter or Save saves, Esc or Cancel puts the name back. The new name shows at
 * once (optimistic) and the previous one comes back if the API refuses, with the
 * reason under the reopened field.
 */
export function RoomNameHeading({
  roomId,
  name,
  canRename,
}: {
  roomId: string;
  name: string;
  canRename: boolean;
}) {
  const rename = useRenameRoom(roomId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  // A local problem (empty / too long) or the API's refusal of the last save.
  const [problem, setProblem] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  function start() {
    setDraft(name);
    setProblem(null);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setProblem(null);
  }

  function save() {
    const local = roomNameProblem(draft);
    if (local) {
      setProblem(local);
      return;
    }
    const next = draft.trim();
    setEditing(false);
    setProblem(null);
    if (next === name) return;
    rename.mutate(
      { name: next },
      {
        // The list rolled back to the old name (useRenameRoom); reopen the field
        // with what was typed and say why it was refused.
        onError: (error) => {
          setDraft(next);
          setProblem(apiMessage(error) ?? 'The room could not be renamed.');
          setEditing(true);
        },
      },
    );
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      save();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      cancel();
    }
  }

  if (editing) {
    return (
      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          save();
        }}
        aria-label="Rename room"
        className="flex flex-col gap-1.5"
        noValidate
      >
        <div className="flex flex-wrap items-center gap-2">
          <Input
            ref={inputRef}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (problem) setProblem(null);
            }}
            onKeyDown={onKeyDown}
            aria-label="Room name"
            aria-invalid={problem ? true : undefined}
            aria-describedby={problem ? 'room-name-problem' : undefined}
            className="h-10 min-w-0 flex-1 font-serif text-xl font-semibold md:max-w-xl"
          />
          <Button type="submit" size="sm">
            Save
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={cancel}>
            Cancel
          </Button>
        </div>
        {problem ? (
          <p id="room-name-problem" role="alert" className="text-[13px] text-destructive">
            {problem}
          </p>
        ) : null}
      </form>
    );
  }

  return (
    <div className="flex items-start gap-1.5">
      <h1 className={HEADING_CLASS}>{name}</h1>
      {canRename ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={start}
          aria-label="Rename room"
          title="Rename room"
          className="mt-0.5 shrink-0 px-2 text-ink-2"
        >
          <Icon name="pen" size={15} />
        </Button>
      ) : null}
    </div>
  );
}
